import React from "react"
import { useNavigate } from "react-router-dom"
import { useTable, useSortBy, usePagination, useFilters, useGlobalFilter } from "react-table"
import Emoji from "components/shared/Emoji"
import { useTranslation } from "react-i18next"

import { profilPartnerStatusList } from "utils/constants"
import FormElement from "components/Form/FormElement"
import { useAuth } from "hooks/useAuth"
import { privateRoutes } from "router/routes"

const PAGINATION_LENGTH = 10

const statusFilter = [{ value: -1, label: "Tous", id: "all" }, ...profilPartnerStatusList]

const CellStatus = (row) => {
	const statusClass = row.value.replace(/\s+/g, "-").toString().toLowerCase()
	return <div className={"dot " + statusClass}>{row.value}</div>
}

export const GenderTable = ({ partnersList, className }) => {
	const [data, setData] = React.useState(partnersList)
	const [filter, setFilter] = React.useState(statusFilter[0])
	const { t } = useTranslation()
	const { user } = useAuth()
	const navigate = useNavigate()

	// Set status 0 whan when change gender
	React.useEffect(() => {
		setFilter(statusFilter[0])
		setData(partnersList)
	}, [partnersList])

	const getStatusLabel = (row) => {
		return profilPartnerStatusList.find((item) => item.id === row.status)?.label || ""
	}

	// Columns definition
	const columns = React.useMemo(
		() => [
			{
				Header: t("profil.status", { ns: "team" }),
				accessor: getStatusLabel,
				Cell: CellStatus,
			},
			{
				Header: t("profil.social_reason", { ns: "partner" }),
				accessor: "social_reason",
			},
			{
				Header: t("profil.commercial_name", { ns: "partner" }),
				accessor: "commercial_name",
			},
			{
				Header: t("profil.siret", { ns: "partner" }),
				accessor: "siret",
			},
			{
				Header: t("profil.postal_code", { ns: "partner" }),
				accessor: "postal_code",
			},
			{
				Header: t("profil.town", { ns: "partner" }),
				accessor: "town",
			},
			{
				Header: t("profil.client_number", { ns: "partner" }),
				accessor: "client_number",
			},
			{
				Header: t("profil.e-mail", { ns: "partner" }),
				accessor: "email",
			},
			{
				Header: t("profil.phone", { ns: "partner" }),
				accessor: "phone",
			},
			{
				Header: t("list.managed_by", { ns: "partner" }),
				accessor: "managed_by",
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		// rows,
		page, // the rows for the active page

		// pagination utilities
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		setGlobalFilter,
		state: { pageIndex },
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
			},
			// autoResetSelectedRows: false
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		usePagination
	)

	const getColumnSize = React.useCallback((columnName) => {
		// sum should be 24
		switch (columnName) {
			default:
				return "flex-initial"
		}
	}, [])

	// Manage global search
	// -------------------
	const [dataTableFilter, setDataTableFilter] = React.useState("")
	React.useEffect(() => setGlobalFilter(dataTableFilter), [dataTableFilter, setGlobalFilter])

	// Manage pagination
	// -------------------
	const middleDistance = Math.round((PAGINATION_LENGTH - 1) / 2)
	const pageShouldBeHiddenInPagination = (index) => {
		// in the first pages,
		// pagination should not exceed PAGINATION_LENGTH
		const condition1 = pageIndex <= middleDistance && index > PAGINATION_LENGTH - 1
		// in the middle pages,
		// show x pages before currentPage and y pages after currentPage
		const condition2 =
			pageIndex > middleDistance &&
			pageIndex < pageOptions.length - middleDistance &&
			(index < pageIndex - middleDistance || index > pageIndex + middleDistance - 1)
		// in the last pages,
		// always show the x last pages
		const condition3 =
			pageIndex > pageOptions.length - 1 - middleDistance &&
			index < pageOptions.length - PAGINATION_LENGTH
		return condition1 || condition2 || condition3
	}

	const goToPartnerPage = (row) => {
		if (row.original.client_number)
			navigate(`${privateRoutes.partner.basePath}/${row.original?.client_number}`)
	}

	// Filter by status
	React.useEffect(() => {
		setData(
			typeof filter === "number" && filter > -1
				? partnersList.filter((d) => d?.status === filter)
				: partnersList
		)
	}, [filter, partnersList])

	return (
		<>
			<div className={"c-table-team " + (className ? className : "")}>
				<div className="c-table-team__header md:mb-0 pb-7">
					<div className="flex flex-col md:flex-row justify-between mb-7 md:mb-0">
						<div className="l-grid">
							{/* Global filter */}
							<div className="lg:w-1/2 w-full">
								<div className={"c-form-group c-form-group--picto mb-7 md:mb-0"}>
									<label htmlFor="tableglobalFilter" className="c-label mb-1">
										{t("list.search", { ns: "partner" })}
									</label>
									<svg
										className="c-input-picto"
										xmlns="http://www.w3.org/2000/svg"
										height="20"
										width="20"
										viewBox="0 0 512 512"
									>
										<path d="M505.749 475.587l-145.6-145.6c28.203-34.837 45.184-79.104 45.184-127.317C405.333 90.926 314.41.003 202.666.003S0 90.925 0 202.669s90.923 202.667 202.667 202.667c48.213 0 92.48-16.981 127.317-45.184l145.6 145.6c4.16 4.16 9.621 6.251 15.083 6.251s10.923-2.091 15.083-6.251c8.341-8.341 8.341-21.824-.001-30.165zM202.667 362.669c-88.235 0-160-71.765-160-160s71.765-160 160-160 160 71.765 160 160-71.766 160-160 160z" />
									</svg>
									<input
										className={"c-input pl-12"}
										id={"tableglobalFilter"}
										name={"tableglobalFilter"}
										type={"text"}
										placeholder={"nom, raison, sociale..."}
										value={dataTableFilter}
										style={{ maxWidth: "400px" }}
										onChange={(event) => setDataTableFilter(event.target.value)}
									/>
								</div>
							</div>

							<div className="lg:w-1/2 w-full">
								<div className={"c-form-group c-form-group--picto mb-7 md:mb-0"}>
									<FormElement
										value={filter}
										options={statusFilter}
										name={filter}
										type="select"
										label={t("list.select", { ns: "partner" })}
										style={{ minWidth: "300px" }}
										className=""
										required={false}
										onChange={(e) => setFilter(e?.target?.value)}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				{data && (
					<>
						<div className="c-table-wrapper pb-7">
							<table
								className="c-table c-table--responsive c-table-overflow-scroll"
								{...getTableProps()}
							>
								<thead>
									{headerGroups.map((headerGroup) => (
										<tr
											className="c-table__row"
											{...headerGroup.getHeaderGroupProps()}
										>
											{headerGroup.headers.map((column) => {
												return (
													<th
														className={
															"c-table__cell p-5 " +
															getColumnSize(column?.id)
														}
														{...column.getHeaderProps(
															column.getSortByToggleProps()
														)}
													>
														<span>{column.render("Header")}</span>
														<span>
															{column.isSorted
																? column.isSortedDesc
																	? " 🔽"
																	: " 🔼"
																: ""}
														</span>
													</th>
												)
											})}
										</tr>
									))}
								</thead>

								<tbody {...getTableBodyProps()}>
									{page.map((row) => {
										const isActive = user?.user_id === row.original?.userid
										prepareRow(row)

										return (
											<tr
												area-role="button"
												onClick={() => goToPartnerPage(row)}
												className={
													"c-table__row p-5 lg:p-0 " +
													(isActive ? "font-bold text-primary" : "")
												}
												{...row.getRowProps()}
											>
												{row.cells.map((cell) => {
													return (
														<td
															className={
																"c-table__cell md:p-5 " +
																getColumnSize(cell?.column?.id)
															}
															{...cell.getCellProps()}
														>
															<span>{cell.render("Cell")}</span>
														</td>
													)
												})}
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
						{(!data || data?.length === 0) && (
							<p className="text-center mt-5">
								{t("table.no_result", { ns: "team" })}{" "}
								<Emoji label="visage triste" symbol="😟" />
							</p>
						)}

						{/* Pagination */}
						{pageOptions && pageOptions.length > 1 && (
							<div className="flex justify-center mt-5">
								<div className="c-pagination">
									<button
										className="c-btn c-btn--primary px-2 mx-5"
										onClick={() => gotoPage(0)}
										disabled={!canPreviousPage}
									>
										{"<<"}
									</button>
									{pageOptions.map((index) => {
										if (pageShouldBeHiddenInPagination(index))
											return <React.Fragment key={index}></React.Fragment>
										return (
											<button
												type="button"
												key={index}
												className={index === pageIndex ? "font-bold" : ""}
												style={{ width: "30px" }}
												onClick={() => gotoPage(index)}
											>
												{index + 1}
											</button>
										)
									})}
									<button
										className="c-btn c-btn--primary px-2 mx-5"
										onClick={() => gotoPage(pageCount - 1)}
										disabled={!canNextPage}
									>
										{">>"}
									</button>
								</div>
							</div>
						)}
					</>
				)}
			</div>
		</>
	)
}
